import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Typography, message, Layout, Checkbox, Space, Tooltip, Row, Col } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined, SafetyCertificateOutlined, InfoCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Content } = Layout;

const Login = () => {
    const [loading, setLoading] = useState(false);
    const [logoUrl, setLogoUrl] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        axios.get('/api/branding').then(res => {
            if (res.data.logo_url) setLogoUrl(res.data.logo_url);
        }).catch(() => { });
    }, []);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const res = await axios.post('/api/auth/login', {
                username: values.username,
                password: values.password
            });

            localStorage.setItem('token', res.data.token);
            localStorage.setItem('user', JSON.stringify(res.data.user));

            if (values.remember) {
                localStorage.setItem('remembered_user', values.username);
            } else {
                localStorage.removeItem('remembered_user');
            }

            message.success(`Welcome back, ${res.data.user.username}`);
            navigate('/');
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.error || 'Login Failed');
        } finally {
            setLoading(false);
        }
    };

    const initialUsername = localStorage.getItem('remembered_user') || '';

    return (
        <>
            <style>
                {`
                    @keyframes slideIn {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes blob {
                        0% { transform: translate(0px, 0px) scale(1); }
                        33% { transform: translate(30px, -50px) scale(1.1); }
                        66% { transform: translate(-20px, 20px) scale(0.9); }
                        100% { transform: translate(0px, 0px) scale(1); }
                    }
                    .login-container {
                        min-height: 100vh;
                        position: relative;
                        overflow: hidden;
                        background: #f8fafc;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 20px;
                    }
                    .blob-1 {
                        position: absolute;
                        top: -10%;
                        left: -10%;
                        width: 500px;
                        height: 500px;
                        background: #3b82f6;
                        filter: blur(80px);
                        opacity: 0.15;
                        border-radius: 50%;
                        animation: blob 7s infinite alternate;
                    }
                    .blob-2 {
                        position: absolute;
                        bottom: -10%;
                        right: -10%;
                        width: 400px;
                        height: 400px;
                        background: #10b981;
                        filter: blur(80px);
                        opacity: 0.1;
                        border-radius: 50%;
                        animation: blob 10s infinite alternate-reverse;
                    }
                    .login-card {
                        width: 100%;
                        max-width: 460px;
                        border-radius: 20px;
                        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                        border: 1px solid rgba(255, 255, 255, 0.5);
                        background: rgba(255, 255, 255, 0.8) !important;
                        backdrop-filter: blur(8px);
                        animation: slideIn 0.6s ease-out forwards;
                    }
                    .login-card .ant-card-body {
                        padding: 40px !important;
                    }
                    .login-btn {
                        height: 48px !important;
                        font-weight: 600 !important;
                        font-size: 16px !important;
                        border-radius: 10px !important;
                        background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
                        border: none !important;
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
                    }
                    .login-btn:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
                    }
                    .login-btn:active {
                        transform: scale(0.98);
                    }
                    .input-premium .ant-input-affix-wrapper {
                        border-radius: 10px !important;
                        padding: 10px 15px !important;
                        transition: all 0.3s ease;
                    }
                    .input-premium .ant-input-affix-wrapper-focused {
                        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1) !important;
                        border-color: #3b82f6 !important;
                    }
                    .logo-text {
                        background: linear-gradient(to right, #2563eb, #3b82f6);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                        font-weight: 800;
                        letter-spacing: -1px;
                    }
                `}
            </style>

            <div className="login-container">
                <div className="blob-1" />
                <div className="blob-2" />

                <Card className="login-card">
                    <div style={{ textAlign: 'center', marginBottom: 40 }}>
                        <div style={{ marginBottom: 16 }}>
                            {logoUrl ? (
                                <img src={`/api${logoUrl}`} alt="logo" style={{ maxHeight: 60, width: 'auto' }} />
                            ) : (
                                <Space align="center">
                                    <SafetyCertificateOutlined style={{ fontSize: 32, color: '#2563eb' }} />
                                    <Title level={2} className="logo-text" style={{ margin: 0 }}>MAKER AI POS</Title>
                                </Space>
                            )}
                        </div>
                        <Title level={3} style={{ margin: 0, fontWeight: 700 }}>Welcome Back</Title>
                        <Text type="secondary" style={{ fontSize: 14 }}>Secure access to your POS & Accounting system</Text>
                    </div>

                    <Form
                        name="login_form"
                        onFinish={onFinish}
                        layout="vertical"
                        size="large"
                        initialValues={{ username: initialUsername, remember: !!initialUsername }}
                        className="input-premium"
                    >
                        <Form.Item
                            label={<Text strong style={{ fontSize: 13 }}>USERNAME</Text>}
                            name="username"
                            rules={[{ required: true, message: 'Your username is required' }]}
                        >
                            <Input
                                prefix={<UserOutlined style={{ color: '#94a3b8' }} />}
                                placeholder="Enter your Dolibarr username"
                            />
                        </Form.Item>

                        <Form.Item
                            label={<Text strong style={{ fontSize: 13 }}>PASSWORD</Text>}
                            name="password"
                            rules={[{ required: true, message: 'Password cannot be empty' }]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
                                placeholder="••••••••"
                            />
                        </Form.Item>

                        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                            <Col>
                                <Form.Item name="remember" valuePropName="checked" noStyle>
                                    <Checkbox><Text type="secondary" style={{ fontSize: 13 }}>Remember me</Text></Checkbox>
                                </Form.Item>
                            </Col>
                            <Col>
                                <Tooltip title="Contact your system administrator to reset your password.">
                                    <Text type="secondary" style={{ fontSize: 13, cursor: 'pointer' }}>
                                        Forgot password? <InfoCircleOutlined style={{ fontSize: 12 }} />
                                    </Text>
                                </Tooltip>
                            </Col>
                        </Row>

                        <Form.Item style={{ marginBottom: 0 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<LoginOutlined />}
                                loading={loading}
                                block
                                className="login-btn"
                            >
                                Sign In
                            </Button>
                        </Form.Item>
                    </Form>

                    <div style={{ textAlign: 'center', marginTop: 40 }}>
                        <Text type="secondary" style={{ fontSize: 12, opacity: 0.6 }}>
                            Maker AI POS Enterprise Edition v1.2.4
                        </Text>
                    </div>
                </Card>
            </div>
        </>
    );
};

export default Login;
